import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { faTimes, faTriangleExclamation } from '@/lib/fontAwesomePro/solid'
import { cn } from '@/lib/utils'

interface Props {
  value: string
  isValid: boolean
  onClickRemove: () => void
  onChange: (newValue: string) => void
}

const EmailAddress: React.FC<Props> = ({
  value,
  isValid,
  onClickRemove,
  onChange,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const editableElemRef = useRef<HTMLDivElement>(null)
  const variant = isValid ? 'outline' : 'destructive'

  function beginEditing() {
    setIsEditing(true)
    editableElemRef.current?.focus()
  }

  return (
    <span className={cn('inline-flex items-center rounded-sm leading-2')}>
      <ButtonGroup>
        <Button
          variant={variant}
          className={cn('flex items-center rounded-none pr-2 font-medium', {
            'border-primary border bg-white': isEditing,
            'text-black': isValid,
          })}
          title={
            isValid
              ? 'Edit'
              : 'This is not a valid email address. Click to edit'
          }
          onClick={beginEditing}
          onFocus={beginEditing}
          size="sm"
        >
          {!isValid && (
            <span>
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </span>
          )}

          <span
            contentEditable={isEditing}
            className="outline-none"
            suppressContentEditableWarning
            ref={editableElemRef}
            onChange={(e) => {
              onChange(e.currentTarget.textContent ?? '')
            }}
            onBlur={(e) => {
              onChange(e.currentTarget.textContent ?? '')
              setIsEditing(false)
            }}
            onKeyDown={(e) => {
              switch (e.key) {
                case 'Backspace':
                case 'Delete':
                  if ((e.currentTarget.textContent ?? '').length === 0) {
                    // pressing backspace and there's no text in there - just delete it
                    onClickRemove()
                  }

                  break

                case 'Enter':
                  e.preventDefault()
                  e.bubbles = false

                  setIsEditing(false)
                  onChange(e.currentTarget.textContent ?? '')
              }
            }}
          >
            {value}
          </span>
        </Button>

        {/* Remove button */}
        <Button
          variant={variant}
          title="Remove"
          className="rounded-none"
          onClick={onClickRemove}
          size="sm"
        >
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </ButtonGroup>
    </span>
  )
}

export default EmailAddress
